import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { 
  budgetService, 
  categoryService 
} from '../../../apiServices';
import { useRefresh } from '../../../contexts/RefreshContext';
import { 
  Budget, 
  Category, 
  CreateBudgetRequest,
  CreateCategoryRequest 
} from '../../../apiServices/types';
import { formatCurrency, getBudgetTypeText, ValidationConstants, ValidationUtils, formatDateOnlyLocal, parseDateOnlyLocal, evaluateMathExpression, toLocalDateString, toUTCDateString } from '../../../utils';
import { useNotification } from '../../../contexts/NotificationContext';
import './BudgetsPage.css';
import { Modal, GlassSelect, ConfirmationModal } from '../../UI';
import BudgetTemplateModal from '../../BudgetTemplateModal';

interface BudgetCategoryForm {
  categoryId: string;
  plannedAmount: number | undefined;
}

const BudgetsPage: React.FC = () => {
  const { showNotification } = useNotification();
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [, setError] = useState<string>('');
  const [pageNumber, setPageNumber] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 10;
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingBudgetId, setEditingBudgetId] = useState<string>('');
  const [budgetCategories, setBudgetCategories] = useState<BudgetCategoryForm[]>([]);
  const [selectedBudgetType, setSelectedBudgetType] = useState<string>('Weekly'); 
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [isDeletingBudget, setIsDeletingBudget] = useState<Record<string, boolean>>({});
  const [showConfirmClose, setShowConfirmClose] = useState<boolean>(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState<string>('');
  const [showTemplateModal, setShowTemplateModal] = useState<boolean>(false);
  const [collapsedBudgets, setCollapsedBudgets] = useState<Set<string>>(new Set());
  const [loadingCategories, setLoadingCategories] = useState<boolean>(false);
  const [totalAmount, setTotalAmount] = useState<number | undefined>(undefined);
  const [isTotalAmountManuallySet, setIsTotalAmountManuallySet] = useState<boolean>(false);
  const [totalAmountInput, setTotalAmountInput] = useState<string>('');
  const [totalAmountExpression, setTotalAmountExpression] = useState<string>('');
  const [isTotalAmountFocused, setIsTotalAmountFocused] = useState<boolean>(false);
  const [totalAmountTooltipPosition, setTotalAmountTooltipPosition] = useState<{ top: number; left: number; isBelow: boolean }>({ top: 0, left: 0, isBelow: false });
  const totalAmountInputRef = useRef<HTMLInputElement>(null);
  
  // Стани для калькуляції категорій
  const [categoryExpressions, setCategoryExpressions] = useState<Record<number, string>>({});
  const [categoryFocused, setCategoryFocused] = useState<Record<number, boolean>>({});
  const [categoryTooltipPositions, setCategoryTooltipPositions] = useState<Record<number, { top: number; left: number; isBelow: boolean }>>({});
  const categoryInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  const calculateEndDate = (startDateValue: string, budgetType: string): string => {
    if (!startDateValue) return '';
    
    const start = parseDateOnlyLocal(startDateValue);
    let end = new Date(start);
    
    switch (budgetType) {
      case 'Weekly': 
        end.setDate(start.getDate() + 6); 
        break;
      case 'Monthly': 
        end.setMonth(start.getMonth() + 1);
        end.setDate(end.getDate() - 1); 
        break;
      case 'Yearly': 
        end.setFullYear(start.getFullYear() + 1);
        end.setDate(end.getDate() - 1); 
        break;
      case 'Event': 
        return endDate;
      default:
        return '';
    }
    
    return toLocalDateString(end);
  };

  // Обчислює суму всіх категорій
  const calculateCategoriesSum = (): number => {
    return budgetCategories
      .filter(cat => cat.plannedAmount !== undefined && cat.plannedAmount > 0)
      .reduce((sum, cat) => sum + (cat.plannedAmount || 0), 0);
  };

  // Автозаповнення загальної суми коли змінюються категорії (якщо не вручну встановлена)
  useEffect(() => {
    if (!isTotalAmountManuallySet) {
      const categoriesSum = calculateCategoriesSum();
      if (categoriesSum > 0) {
        setTotalAmount(categoriesSum);
        setTotalAmountInput(String(categoriesSum));
      } else {
        // Якщо немає категорій або сума 0, очищаємо загальну суму
        setTotalAmount(undefined);
        setTotalAmountInput('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budgetCategories, isTotalAmountManuallySet]);

  const loadBudgets = useCallback(async (reset: boolean = false, currentPageNumber?: number): Promise<void> => {
    try {
      if (reset) {
        setLoading(true);
        setBudgets([]);
        setHasMore(true);
        setPageNumber(1);
      } else {
        if (loadingMore) return;
        setLoadingMore(true);
      }

      const pageToLoad = reset ? 1 : (currentPageNumber ?? pageNumber);
      const result = await budgetService.getAllPaged({
        pageNumber: pageToLoad,
        pageSize
      });
      
      if (reset) {
        setBudgets(result.data);
        setPageNumber(2);
      } else {
        setBudgets(prev => [...prev, ...result.data]);
        setPageNumber(prev => prev + 1);
      }
      
      setTotalCount(result.totalCount);
      setTotalPages(result.totalPages);
      setHasMore(result.pageNumber < result.totalPages);
      
      // Ініціалізуємо стан колапсу: всі бюджети за замовчуванням згорнуті
      const initiallyCollapsed = new Set<string>();
      result.data.forEach(budget => {
        // Якщо є категорії, додаємо до колапсованих
        if (budget.budgetCategories.length > 0) {
          initiallyCollapsed.add(budget.id);
        }
      });
      
      setCollapsedBudgets(prev => {
        const updated = new Set(prev);
        result.data.forEach(budget => {
          if (budget.budgetCategories.length > 0) {
            updated.add(budget.id);
          }
        });
        return updated;
      });
    } catch (error) {
      setError('Помилка завантаження бюджетів: ' + (error as Error).message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [pageNumber, pageSize, loadingMore]);

  useEffect(() => {
    loadBudgets(true);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (loadingMore || !hasMore || loading) return;

      // Знаходимо scrollable контейнер .content-area
      const scrollContainer = document.querySelector('.content-area');
      if (!scrollContainer) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;

      if (scrollTop + clientHeight >= scrollHeight - 200) {
        loadBudgets(false, pageNumber);
      }
    };

    const scrollContainer = document.querySelector('.content-area');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [loadBudgets, loadingMore, hasMore, loading, pageNumber]);

  // Завантажуємо категорії коли відкривається модалка
  useEffect(() => {
    if (showModal) {
      loadCategoriesInModal();
    }
  }, [showModal]);

  const loadCategoriesInModal = async (): Promise<void> => {
    try {
      setLoadingCategories(true);
      await loadCategories();
    } catch (error) {
      showNotification('Помилка завантаження категорій', 'error');
    } finally {
      setLoadingCategories(false);
    }
  };


  const loadCategories = async (): Promise<void> => {
    try {
      const data = await categoryService.getAll();
      setCategories(data);
    } catch (error) {
    }
  };

  const handleRefresh = useCallback(async () => {
    await loadBudgets(true);
  }, [loadBudgets]);

  // Тихе оновлення даних без показу loading стану
  const silentRefresh = useCallback(async () => {
    try {
      const result = await budgetService.getAllPaged({
        pageNumber: 1,
        pageSize: Math.max(budgets.length, pageSize)
      });
      setBudgets(result.data);
      setTotalCount(result.totalCount);
      setTotalPages(result.totalPages);
      setHasMore(result.pageNumber < result.totalPages);
      setPageNumber(Math.ceil(result.data.length / pageSize) + 1);
      setCollapsedBudgets(prev => {
        const updated = new Set<string>();
        result.data.forEach(budget => {
          if (budget.budgetCategories.length > 0) {
            updated.add(budget.id);
          }
        });
        return updated;
      });
    } catch {
      // Ігноруємо помилки при тихому оновленні
    }
  }, [budgets.length, pageSize]);

  useRefresh(handleRefresh);

  const showCreateBudgetModal = (): void => {
    setEditingBudgetId('');
    setBudgetCategories([]);
    setSelectedBudgetType('Weekly'); 
    setFormErrors({});
    setTotalAmount(undefined);
    setIsTotalAmountManuallySet(false);
    setTotalAmountInput('');
    setTotalAmountExpression('');
    setIsTotalAmountFocused(false);
    setCategoryExpressions({});
    setCategoryFocused({});
    setCategoryTooltipPositions({});
    
    const today = toLocalDateString(new Date());
    setStartDate(today);
    
    const calculatedEndDate = calculateEndDate(today, 'Weekly');
    setEndDate(calculatedEndDate);
    
    setShowModal(true);
  };

  const hideBudgetModal = (): void => {
    setShowModal(false);
    setEditingBudgetId('');
    setBudgetCategories([]);
    setSelectedBudgetType('Weekly'); 
    setStartDate('');
    setEndDate('');
    setFormErrors({});
    setShowConfirmClose(false);
    setTotalAmount(undefined);
    setIsTotalAmountManuallySet(false);
    setTotalAmountInput('');
    setTotalAmountExpression('');
    setIsTotalAmountFocused(false);
    setCategoryExpressions({});
    setCategoryFocused({});
    setCategoryTooltipPositions({});
    setLoadingCategories(false);
  };

  const handleModalClose = (): void => {
    // Перевіряємо чи є незбережені зміни
    const hasChanges = startDate || endDate || budgetCategories.length > 0;
    
    if (hasChanges) {
      setShowConfirmClose(true);
    } else {
      hideBudgetModal();
    }
  };

  const handleConfirmClose = (): void => {
    hideBudgetModal();
  };

  const handleCancelClose = (): void => {
    setShowConfirmClose(false);
  };

  const handleBudgetTypeChange = (newBudgetType: string): void => {
    setSelectedBudgetType(newBudgetType);
    
    if (startDate) {
      const calculatedEndDate = calculateEndDate(startDate, newBudgetType);
      setEndDate(calculatedEndDate);
    }
  };

  const handleStartDateChange = (newStartDate: string): void => {
    setStartDate(newStartDate);
    
    const calculatedEndDate = calculateEndDate(newStartDate, selectedBudgetType);
    setEndDate(calculatedEndDate);
  };

  const addBudgetCategory = (): void => {
    setBudgetCategories(prev => [
      ...prev,
      { categoryId: '', plannedAmount: undefined }
    ]);
  };

  const removeBudgetCategory = (index: number): void => {
    setBudgetCategories(prev => prev.filter((_, i) => i !== index));
  };

  const updateBudgetCategory = (index: number, field: keyof BudgetCategoryForm, value: string | number | undefined): void => {
    setBudgetCategories(prev => prev.map((cat, i) => 
      i === index ? { ...cat, [field]: value } : cat
    ));
  };

  // Валідація загальної суми після зміни категорій (якщо загальна сума вручну встановлена)
  useEffect(() => {
    if (isTotalAmountManuallySet && totalAmount !== undefined) {
      const categoriesSum = calculateCategoriesSum();
      if (totalAmount < categoriesSum) {
        setFormErrors(prev => ({
          ...prev,
          totalAmount: `Загальна сума не може бути менше суми категорій (${formatCurrency(categoriesSum)})`
        }));
      } else {
        setFormErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.totalAmount;
          return newErrors;
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budgetCategories, isTotalAmountManuallySet, totalAmount]);

  // Обчислюємо результат для загальної суми
  const totalAmountMathResult = useMemo(() => {
    if (!totalAmountExpression) return { result: null, isExpression: false };
    return evaluateMathExpression(totalAmountExpression);
  }, [totalAmountExpression]);
  
  // Обчислюємо результати для категорій
  const categoryMathResults = useMemo(() => {
    const results: Record<number, { result: number | null; isExpression: boolean }> = {};
    Object.keys(categoryExpressions).forEach(index => {
      const expr = categoryExpressions[Number(index)];
      if (expr) {
        results[Number(index)] = evaluateMathExpression(expr);
      }
    });
    return results;
  }, [categoryExpressions]);
  
  // Оновлення позиції тултіпа для загальної суми
  useEffect(() => {
    if (!isTotalAmountFocused || !totalAmountInputRef.current || !totalAmountMathResult.isExpression || totalAmountMathResult.result === null) {
      return;
    }
    
    const updatePosition = () => {
      if (!totalAmountInputRef.current) return;
      
      const inputRect = totalAmountInputRef.current.getBoundingClientRect();
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;
      const viewportWidth = window.innerWidth;
      
      const tooltipWidth = 60;
      const spacing = 6;
      
      const fitsRight = inputRect.right + spacing + tooltipWidth <= viewportWidth;
      
      if (fitsRight) {
        setTotalAmountTooltipPosition({
          top: inputRect.top + scrollY + inputRect.height / 2,
          left: inputRect.right + scrollX + spacing,
          isBelow: false
        });
      } else {
        setTotalAmountTooltipPosition({
          top: inputRect.bottom + scrollY + 4,
          left: inputRect.left + scrollX,
          isBelow: true
        });
      }
    };
    
    updatePosition();
    
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isTotalAmountFocused, totalAmountMathResult.isExpression, totalAmountMathResult.result]);
  
  // Оновлення позицій тултіпів для категорій
  useEffect(() => {
    const focusedIndices = Object.keys(categoryFocused).filter(idx => 
      categoryFocused[Number(idx)] && 
      categoryInputRefs.current[Number(idx)] && 
      categoryMathResults[Number(idx)]?.isExpression && 
      categoryMathResults[Number(idx)]?.result !== null
    );
    
    if (focusedIndices.length === 0) return;
    
    const updatePositions = () => {
      focusedIndices.forEach(indexStr => {
        const idx = Number(indexStr);
        const inputRef = categoryInputRefs.current[idx];
        if (!inputRef) return;
        
        const inputRect = inputRef.getBoundingClientRect();
        const scrollY = window.scrollY;
        const scrollX = window.scrollX;
        const viewportWidth = window.innerWidth;
        
        const tooltipWidth = 60;
        const spacing = 6;
        
        const fitsRight = inputRect.right + spacing + tooltipWidth <= viewportWidth;
        
        setCategoryTooltipPositions(prev => ({
          ...prev,
          [idx]: fitsRight ? {
            top: inputRect.top + scrollY + inputRect.height / 2,
            left: inputRect.right + scrollX + spacing,
            isBelow: false
          } : {
            top: inputRect.bottom + scrollY + 4,
            left: inputRect.left + scrollX,
            isBelow: true
          }
        }));
      });
    };
    
    updatePositions();
    
    window.addEventListener('scroll', updatePositions, true);
    window.addEventListener('resize', updatePositions);
    
    return () => {
      window.removeEventListener('scroll', updatePositions, true);
      window.removeEventListener('resize', updatePositions);
    };
  }, [categoryFocused, categoryMathResults]);
  
  const handleTotalAmountChange = (value: string): void => {
    // Зберігаємо введений текст завжди
    setTotalAmountInput(value);
    setTotalAmountExpression(value);
    
    // Якщо поле порожнє, повертаємось до автозаповнення
    if (value.trim() === '') {
      setIsTotalAmountManuallySet(false);
      setTotalAmount(undefined);
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.totalAmount;
        return newErrors;
      });
      return;
    }
    
    // Перевіряємо чи це математичний вираз
    const evalResult = evaluateMathExpression(value);
    if (!evalResult.isExpression) {
      // Просто число
      const numValue = parseFloat(value);
      const categoriesSum = calculateCategoriesSum();
      
      setIsTotalAmountManuallySet(true);
      
      if (!isNaN(numValue)) {
        setTotalAmount(numValue);
        
        if (numValue < categoriesSum) {
          setFormErrors(prev => ({
            ...prev,
            totalAmount: `Загальна сума не може бути менше суми категорій (${formatCurrency(categoriesSum)})`
          }));
        } else {
          setFormErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.totalAmount;
            return newErrors;
          });
        }
      }
    }
  };
  
  const handleTotalAmountFocus = (): void => {
    setIsTotalAmountFocused(true);
    setTotalAmountExpression(totalAmountInput);
  };
  
  const handleTotalAmountBlur = (): void => {
    setIsTotalAmountFocused(false);
    
    // Якщо інпут порожній, дозволяємо порожнє значення
    if (totalAmountExpression.trim() === '') {
      setIsTotalAmountManuallySet(false);
      setTotalAmount(undefined);
      setTotalAmountInput('');
      setTotalAmountExpression('');
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.totalAmount;
        return newErrors;
      });
      return;
    }
    
    // Застосовуємо результат обчислення якщо є
    if (totalAmountMathResult && totalAmountMathResult.isExpression && totalAmountMathResult.result !== null) {
      const categoriesSum = calculateCategoriesSum();
      setIsTotalAmountManuallySet(true);
      setTotalAmount(totalAmountMathResult.result);
      setTotalAmountInput(String(totalAmountMathResult.result));
      
      if (totalAmountMathResult.result < categoriesSum) {
        setFormErrors(prev => ({
          ...prev,
          totalAmount: `Загальна сума не може бути менше суми категорій (${formatCurrency(categoriesSum)})`
        }));
      } else {
        setFormErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.totalAmount;
          return newErrors;
        });
      }
    }
    
    setTotalAmountExpression('');
  };
  
  const handleCategoryAmountChange = (index: number, value: string): void => {
    setCategoryExpressions(prev => ({ ...prev, [index]: value }));
    
    // Якщо інпут порожній, встановлюємо undefined
    if (value.trim() === '') {
      updateBudgetCategory(index, 'plannedAmount', undefined);
      return;
    }
    
    const evalResult = evaluateMathExpression(value);
    if (!evalResult.isExpression) {
      // Просто число
      const numValue = parseFloat(value);
      updateBudgetCategory(index, 'plannedAmount', !isNaN(numValue) ? numValue : undefined);
    }
  };
  
  const handleCategoryAmountFocus = (index: number, currentValue: number | undefined): void => {
    setCategoryFocused(prev => ({ ...prev, [index]: true }));
    setCategoryExpressions(prev => ({ ...prev, [index]: currentValue ? String(currentValue) : '' }));
  };
  
  const handleCategoryAmountBlur = (index: number): void => {
    setCategoryFocused(prev => ({ ...prev, [index]: false }));
    
    const expression = categoryExpressions[index] || '';
    const mathResult = categoryMathResults[index];
    
    // Якщо інпут порожній, дозволяємо порожнє значення
    if (expression.trim() === '') {
      updateBudgetCategory(index, 'plannedAmount', undefined);
      setCategoryExpressions(prev => {
        const newExpressions = { ...prev };
        delete newExpressions[index];
        return newExpressions;
      });
      return;
    }
    
    // Якщо є результат обчислення, застосовуємо його
    if (mathResult && mathResult.isExpression && mathResult.result !== null) {
      updateBudgetCategory(index, 'plannedAmount', mathResult.result);
    }
    
    // Очищаємо вираз
    setCategoryExpressions(prev => {
      const newExpressions = { ...prev };
      delete newExpressions[index];
      return newExpressions;
    });
  };

  // Перевіряє чи є розсинхрон між загальною сумою та сумою категорій
  const hasTotalAmountMismatch = (): boolean => {
    if (!isTotalAmountManuallySet || totalAmountInput.trim() === '') {
      return false;
    }
    
    const categoriesSum = calculateCategoriesSum();
    if (categoriesSum === 0) {
      return false;
    }
    
    // Перевіряємо чи введене значення відрізняється від суми категорій
    const inputValue = parseFloat(totalAmountInput);
    if (isNaN(inputValue)) {
      // Якщо значення невалідне, але є категорії - показуємо кнопку
      return categoriesSum > 0;
    }
    
    // Перевіряємо розсинхрон більше за 0.01
    return Math.abs(inputValue - categoriesSum) > 0.01;
  };

  const handleSyncTotalAmount = (): void => {
    const categoriesSum = calculateCategoriesSum();
    if (categoriesSum > 0) {
      setTotalAmount(categoriesSum);
      setTotalAmountInput(String(categoriesSum));
      setIsTotalAmountManuallySet(false);
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.totalAmount;
        return newErrors;
      });
    }
  };

  const handleCreateCategory = async (categoryName: string, categoryIndex: number): Promise<void> => {
    try {
      if (categoryName.length < ValidationConstants.Category.NameMinLength) {
        setFormErrors({ general: `Назва категорії має бути не менше ${ValidationConstants.Category.NameMinLength} символів` });
        showNotification(`Назва категорії має бути не менше ${ValidationConstants.Category.NameMinLength} символів`, 'error');
        return;
      }
      if (categoryName.length > ValidationConstants.Category.NameMaxLength) {
        setFormErrors({ general: `Назва категорії не може перевищувати ${ValidationConstants.Category.NameMaxLength} символів` });
        showNotification(`Назва категорії не може перевищувати ${ValidationConstants.Category.NameMaxLength} символів`, 'error');
        return;
      }
      if (ValidationUtils.containsForbiddenChars(categoryName)) {
        setFormErrors({ general: 'Назва категорії містить заборонені символи (<, >, ", &, \')' });
        showNotification('Назва категорії містить заборонені символи', 'error');
        return;
      }

      const existingCategory = categories.find(cat => cat.name.toLowerCase() === categoryName.toLowerCase());
      if (existingCategory) {
        updateBudgetCategory(categoryIndex, 'categoryId', existingCategory.id);
        return;
      }

      const newCategoryRequest: CreateCategoryRequest = { 
        name: categoryName.trim()
      };
      const newCategory = await categoryService.create(newCategoryRequest);
      
      setCategories(prev => [...prev, newCategory]);
      
      updateBudgetCategory(categoryIndex, 'categoryId', newCategory.id);
      showNotification('Категорію успішно створено', 'success');
    } catch (error: any) {
      setFormErrors({ general: error.message || 'Помилка створення категорії' });
      showNotification('Помилка створення категорії', 'error');
    }
  };

  const saveBudget = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);

    const categories = budgetCategories.filter(cat => cat.categoryId && cat.plannedAmount && cat.plannedAmount > 0);
    const categoriesSum = calculateCategoriesSum();
    
    // Валідація перед збереженням
    const finalTotalAmount = totalAmount !== undefined ? totalAmount : categoriesSum;
    if (finalTotalAmount < categoriesSum) {
      setFormErrors({
        totalAmount: `Загальна сума не може бути менше суми категорій (${formatCurrency(categoriesSum)})`
      });
      showNotification('Загальна сума не може бути менше суми категорій', 'error');
      return;
    }

    const budgetData: CreateBudgetRequest = {
      name: formData.get('budget-name') as string,
      budgetType: selectedBudgetType,
      startDate: toUTCDateString(startDate),
      endDate: toUTCDateString(endDate, '23:59:59'),
      totalPlannedAmount: finalTotalAmount,
      budgetCategories: categories.map(cat => ({
        categoryId: cat.categoryId,
        plannedAmount: cat.plannedAmount!
      }))
    };

    setIsLoading(true);
    setError('');
    setFormErrors({});

    try {
      if (editingBudgetId) {
        await budgetService.update(editingBudgetId, budgetData);
      } else {
        await budgetService.create(budgetData);
      }

      hideBudgetModal();
      await silentRefresh();
      showNotification(
        editingBudgetId ? 'Бюджет успішно оновлено' : 'Бюджет успішно створено',
        'success'
      );
    } catch (error: any) {
      setFormErrors({
        general: error.message || 'Помилка збереження бюджету'
      });
      showNotification('Помилка збереження бюджету', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const editBudget = (budgetId: string): void => {
    const budget = budgets.find(b => b.id === budgetId);
    if (!budget) return;

    setEditingBudgetId(budgetId);
    setSelectedBudgetType(budget.budgetType);
    setStartDate(budget.startDate.split('T')[0]);
    setEndDate(budget.endDate.split('T')[0]);
    setBudgetCategories(budget.budgetCategories.map(cat => ({
      categoryId: cat.categoryId,
      plannedAmount: cat.plannedAmount
    })));
    setTotalAmount(budget.totalPlannedAmount);
    setTotalAmountInput(String(budget.totalPlannedAmount));
    
    // Перевіряємо чи загальна сума співпадає з сумою категорій
    const categoriesSum = budget.budgetCategories.reduce((sum, cat) => sum + cat.plannedAmount, 0);
    const matchesCategories = Math.abs(budget.totalPlannedAmount - categoriesSum) < 0.01;
    
    // Якщо сума співпадає - активуємо автозаповнення, інакше вважаємо що встановлено вручну
    setIsTotalAmountManuallySet(!matchesCategories);
    setFormErrors({});
    
    setShowModal(true);
  };

  const deleteBudget = (budgetId: string): void => {
    if (isDeletingBudget[budgetId]) return;
    setShowConfirmDelete(budgetId);
  };

  const handleConfirmDelete = async (): Promise<void> => {
    const budgetId = showConfirmDelete;
    if (!budgetId) return;
    
    setIsDeletingBudget(prev => ({ ...prev, [budgetId]: true }));
    
    try {
      await budgetService.delete(budgetId);
      await silentRefresh();
      showNotification('Бюджет успішно видалено', 'success');
    } catch (error: any) {
      showNotification(error.message || 'Помилка видалення бюджету', 'error');
    } finally {
      setIsDeletingBudget(prev => ({ ...prev, [budgetId]: false }));
      setShowConfirmDelete('');
    }
  };

  const handleCancelDelete = (): void => {
    setShowConfirmDelete('');
  };

  const isBudgetActive = (budget: Budget): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = parseDateOnlyLocal(budget.startDate);
    startDate.setHours(0, 0, 0, 0);
    const endDate = parseDateOnlyLocal(budget.endDate);
    endDate.setHours(23, 59, 59, 999);
    
    return today >= startDate && today <= endDate;
  };

  const toggleBudgetCollapse = (budgetId: string): void => {
    setCollapsedBudgets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(budgetId)) {
        newSet.delete(budgetId);
      } else {
        newSet.add(budgetId);
      }
      return newSet;
    });
  };


  if (loading) {
    return (
      <div className="budgets-page">
        <div className="card">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Завантаження планування...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="budgets-page">
      <div className="budgets-page-header">
        <div className="budgets-page-header-content">
          <h2 className="budgets-page-header-title">Планування бюджету</h2>
          <p className="budgets-page-header-subtitle">Плануйте і контролюйте свої витрати</p>
        </div>
        <div className="budgets-page-header-actions">
          <button className="budgets-page-header-button" onClick={() => setShowTemplateModal(true)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7"></rect>
              <rect x="14" y="3" width="7" height="7"></rect>
              <rect x="14" y="14" width="7" height="7"></rect>
              <rect x="3" y="14" width="7" height="7"></rect>
            </svg>
            <span>Шаблон</span>
          </button>
          <button className="budgets-page-header-button" onClick={showCreateBudgetModal}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2v20M2 12h20"></path>
            </svg>
            <span>Створити</span>
          </button>
        </div>
      </div>

      <div className="budgets-list">
        {budgets.length === 0 ? (
          <div className="empty-state card">
            <div className="empty-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 3v18h18"></path>
                <path d="M18 17V9"></path>
                <path d="M13 17V5"></path>
                <path d="M8 17v-3"></path>
              </svg>
            </div>
            <div className="empty-title">Немає бюджетів</div>
            <div className="empty-text">Створіть свій перший бюджет для контролю витрат</div>
          </div>
        ) : (
          budgets.map(budget => {
            const progress = budget.totalPlannedAmount > 0 ? (budget.totalActualAmount / budget.totalPlannedAmount) * 100 : 0;
            const totalSpent = budget.totalActualAmount;
            const remaining = budget.totalPlannedAmount - totalSpent;
            const isActive = isBudgetActive(budget);
            const hasCategories = budget.budgetCategories.length > 0;
            const isCollapsed = hasCategories && collapsedBudgets.has(budget.id);
            
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const startDate = parseDateOnlyLocal(budget.startDate);
            startDate.setHours(0, 0, 0, 0);
            const endDate = parseDateOnlyLocal(budget.endDate);
            endDate.setHours(23, 59, 59, 999);
            
            let status = 'upcoming';
            if (today >= startDate && today <= endDate) {
              status = 'active';
            } else if (today > endDate) {
              status = 'completed';
            }

            return (
              <div key={budget.id} className={`budget-card budget-card-${status} ${isCollapsed ? 'collapsed' : ''}`}>
                <div className="budget-card-header">
                  <div 
                    className={`budget-card-title-section ${hasCategories ? '' : 'no-collapse'}`}
                    onClick={hasCategories ? () => toggleBudgetCollapse(budget.id) : undefined}
                  >
                    <div className="budget-status-indicator-wrapper">
                      <div className={`budget-status-indicator budget-status-indicator-${status}`}></div>
                      {hasCategories && (
                        <svg 
                          className="budget-expand-icon"
                          width="20" 
                          height="20" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        >
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      )}
                    </div>
                    <div className="budget-title-content">
                      <h3 className="budget-card-title">{budget.name}</h3>
                      <div className="budget-card-meta">
                        <span className="budget-date-range">
                          {formatDateOnlyLocal(budget.startDate)} - {formatDateOnlyLocal(budget.endDate)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="budget-card-actions">
                    <button 
                      className="budget-action-icon" 
                      onClick={(e) => {
                        e.stopPropagation();
                        editBudget(budget.id);
                      }} 
                      title="Редагувати"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </button>
                    <button 
                      className="budget-action-icon budget-action-icon-danger" 
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteBudget(budget.id);
                      }} 
                      disabled={isDeletingBudget[budget.id]}
                      title={isDeletingBudget[budget.id] ? "Видалення..." : "Видалити"}
                    >
                      {isDeletingBudget[budget.id] ? (
                        <div className="spinner-small"></div>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3,6 5,6 21,6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div className="budget-card-body">
                  <div className="budget-stats-grid">
                    <div className="budget-stat">
                      <div className="budget-stat-label">Бюджет</div>
                      <div className="budget-stat-value budget-stat-total">{formatCurrency(budget.totalPlannedAmount)}</div>
                    </div>
                    <div className="budget-stat">
                      <div className="budget-stat-label">Витрачено</div>
                      <div className="budget-stat-value budget-stat-spent">{formatCurrency(totalSpent)}</div>
                    </div>
                    <div className={`budget-stat ${progress > 110 ? 'budget-stat-progress-over' : progress > 100 ? 'budget-stat-progress-warning' : 'budget-stat-progress-good'}`}>
                      <div className="budget-stat-label">Залишилось</div>
                      <div className={`budget-stat-value budget-stat-remaining ${remaining < 0 ? 'budget-stat-over' : ''}`}>
                        {formatCurrency(Math.max(0, remaining))}
                      </div>
                    </div>
                  </div>

                  <div className="budget-progress-wrapper">
                    <div className="budget-progress-header">
                      <span className="budget-progress-label">Прогрес</span>
                      <span className="budget-progress-percentage">{progress.toFixed(1)}%</span>
                    </div>
                    <div className="budget-progress-bar">
                      <div 
                        className={`budget-progress-fill budget-progress-${progress > 110 ? 'over' : progress > 100 ? 'warning' : 'good'}`}
                        style={{ width: `${Math.min(progress, 100)}%` }}
                      ></div>
                      {progress > 100 && (
                        <div 
                          className="budget-progress-overfill"
                          style={{ width: `${progress - 100}%` }}
                        ></div>
                      )}
                    </div>
                  </div>
                </div>

                {hasCategories && (
                  <div className={`budget-categories-section ${isCollapsed ? 'collapsed' : ''}`}>
                    <div className="budget-categories-header">
                      <span className="budget-categories-title">Категорії</span>
                    </div>
                    <div className="budget-categories-list">
                      {budget.budgetCategories
                        .sort((a, b) => {
                          // Спочатку по проценту використання (від меншого до більшого - найбільший процент внизу)
                          if (Math.abs(a.progressPercentage - b.progressPercentage) > 0.01) {
                            return a.progressPercentage - b.progressPercentage;
                          }
                          // Якщо процент співпадає, то по запланованій сумі (від меншої до більшої)
                          return a.plannedAmount - b.plannedAmount;
                        })
                        .map(cat => (
                        <div key={cat.id} className="budget-category-card">
                          <div className="budget-category-header">
                            <span className="budget-category-name">{cat.categoryName}</span>
                            <div className="budget-category-amounts">
                              <span className="budget-category-actual">{formatCurrency(cat.actualAmount)}</span>
                              <span className="budget-category-separator">/</span>
                              <span className="budget-category-planned">{formatCurrency(cat.plannedAmount)}</span>
                            </div>
                          </div>
                          <div className="budget-category-progress">
                            <div 
                              className={`budget-category-progress-fill category-progress-${cat.progressPercentage > 110 ? 'over' : cat.progressPercentage > 100 ? 'warning' : 'good'}`}
                              style={{ width: `${Math.min(cat.progressPercentage, 100)}%` }}
                            ></div>
                            {cat.progressPercentage > 100 && (
                              <div 
                                className="budget-category-progress-overfill"
                                style={{ width: `${cat.progressPercentage - 100}%` }}
                              ></div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}

        {loadingMore && (
          <div className="loading-more">
            <div className="spinner"></div>
            <p>Завантаження...</p>
          </div>
        )}

        {!hasMore && budgets.length > 0 && (
          <div className="end-of-list">
            <p>Всі бюджети завантажено</p>
          </div>
        )}
      </div>

      <Modal
        isOpen={showModal}
        onClose={handleModalClose}
        title={editingBudgetId ? 'Редагувати бюджет' : 'Створити бюджет'}
        size="md"
        footer={(
          <>
            <button type="button" className="btn btn-secondary" onClick={handleModalClose}>
              Скасувати
            </button>
            <button
              type="submit"
              form="budget-form"
              className="btn btn-primary"
              disabled={isLoading}
            >
              {isLoading ? 'Збереження...' : (editingBudgetId ? 'Оновити бюджет' : 'Створити бюджет')}
            </button>
          </>
        )}
      >
        {(() => {
          const currentBudget = editingBudgetId ? budgets.find(b => b.id === editingBudgetId) : undefined;
          return (
            <form id="budget-form" onSubmit={saveBudget}>
          <div className="form-group">
            <label className="form-label" htmlFor="budget-name">Назва бюджету</label>
            <input
              type="text"
              id="budget-name"
              name="budget-name"
              className="form-input"
              required
              minLength={ValidationConstants.Budget.NameMinLength}
              maxLength={ValidationConstants.Budget.NameMaxLength}
              placeholder="Наприклад: Щомісячні витрати"
              defaultValue={currentBudget ? currentBudget.name : ''}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Тип бюджету</label>
            <GlassSelect
              options={[
                { value: 'Weekly', label: 'Тижневий' },
                { value: 'Monthly', label: 'Місячний' },
                { value: 'Yearly', label: 'Річний' },
                { value: 'Event', label: 'Подія' }
              ]}
              value={selectedBudgetType}
              onChange={handleBudgetTypeChange}
              placeholder="Оберіть тип бюджету"
            />
            <input type="hidden" name="budget-type" value={selectedBudgetType} />
          </div>

          <div className="budget-dates-row" style={{ display: 'flex', gap: 'var(--spacing-md)' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label" htmlFor="budget-start-date">Дата початку</label>
              <div className="date-input-wrapper">
                <input
                  type="date"
                  id="budget-start-date"
                  name="budget-start-date"
                  className="form-input date-input-custom"
                  required
                  value={startDate}
                  onChange={(e) => handleStartDateChange(e.target.value)}
                />
                <label htmlFor="budget-start-date" className="date-input-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                </label>
              </div>
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label" htmlFor="budget-end-date">Дата закінчення</label>
              <div className="date-input-wrapper">
                <input
                  type="date"
                  id="budget-end-date"
                  name="budget-end-date"
                  className="form-input date-input-custom"
                  required
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  disabled={selectedBudgetType !== 'Event'} 
                />
                <label htmlFor="budget-end-date" className="date-input-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                </label>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="budget-total-amount">
              Загальна сума
              {!isTotalAmountManuallySet && budgetCategories.length > 0 && (
                <span style={{ fontSize: '0.85em', color: 'var(--text-secondary)', fontWeight: 'normal', marginLeft: '8px' }}>
                  (автозаповнюється)
                </span>
              )}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                ref={totalAmountInputRef}
                type="text"
                id="budget-total-amount"
                name="budget-total-amount"
                className={`form-input ${formErrors.totalAmount ? 'error' : ''}`}
                required
                placeholder="0.00"
                value={isTotalAmountFocused ? totalAmountExpression : totalAmountInput}
                onChange={(e) => handleTotalAmountChange(e.target.value)}
                onFocus={handleTotalAmountFocus}
                onBlur={handleTotalAmountBlur}
                style={{ paddingRight: hasTotalAmountMismatch() ? '40px' : undefined }}
              />
              {hasTotalAmountMismatch() && (
                <button
                  type="button"
                  onClick={handleSyncTotalAmount}
                  className="budget-sync-button"
                  title="Синхронізувати з сумою категорій"
                >
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"></path>
                  </svg>
                </button>
              )}
              {isTotalAmountFocused && totalAmountMathResult.isExpression && totalAmountMathResult.result !== null && createPortal(
                <div 
                  className={`price-calc-tooltip ${totalAmountTooltipPosition.isBelow ? 'below' : ''}`}
                  style={{
                    position: 'absolute',
                    top: totalAmountTooltipPosition.top,
                    left: totalAmountTooltipPosition.left,
                    transform: totalAmountTooltipPosition.isBelow ? 'none' : 'translateY(-50%)',
                    zIndex: 9999999
                  }}
                >
                  <span className="price-calc-tooltip-content">
                    = {totalAmountMathResult.result}
                  </span>
                </div>,
                document.body
              )}
            </div>
            {formErrors.totalAmount && (
              <div className="error-message" style={{ marginTop: '4px' }}>
                {formErrors.totalAmount}
              </div>
            )}
            {!isTotalAmountManuallySet && calculateCategoriesSum() > 0 && (
              <div style={{ fontSize: '0.85em', color: 'var(--text-secondary)', marginTop: '4px' }}>
                Сума категорій: {formatCurrency(calculateCategoriesSum())}
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Категорії та суми</label>
            <div id="budget-categories">
              {budgetCategories.map((category, index) => (
                <div key={index} className="budget-category-item">
                  <div className="budget-category-select">
                    <GlassSelect
                      options={[
                        ...categories.map(cat => ({
                          value: cat.id,
                          label: cat.name
                        }))
                      ]}
                      value={category.categoryId}
                      onChange={(value) => updateBudgetCategory(index, 'categoryId', value)}
                      placeholder="Оберіть категорію"
                      searchable
                      allowCustomValue={true}
                      onCustomValue={(categoryName) => handleCreateCategory(categoryName, index)}
                      disabled={loadingCategories}
                    />
                  </div>
                  <div className="budget-category-amount" style={{ position: 'relative' }}>
                    <input
                      ref={(el) => { categoryInputRefs.current[index] = el; }}
                      type="text"
                      className="form-input budget-category-amount-input"
                      placeholder="0.00"
                      value={categoryFocused[index] ? (categoryExpressions[index] || '') : (category.plannedAmount || '')}
                      onChange={(e) => handleCategoryAmountChange(index, e.target.value)}
                      onFocus={() => handleCategoryAmountFocus(index, category.plannedAmount)}
                      onBlur={() => handleCategoryAmountBlur(index)}
                      required
                    />
                    {categoryFocused[index] && categoryMathResults[index]?.isExpression && categoryMathResults[index]?.result !== null && createPortal(
                      <div 
                        className={`price-calc-tooltip ${categoryTooltipPositions[index]?.isBelow ? 'below' : ''}`}
                        style={{
                          position: 'absolute',
                          top: categoryTooltipPositions[index]?.top || 0,
                          left: categoryTooltipPositions[index]?.left || 0,
                          transform: categoryTooltipPositions[index]?.isBelow ? 'none' : 'translateY(-50%)',
                          zIndex: 9999999
                        }}
                      >
                        <span className="price-calc-tooltip-content">
                          = {categoryMathResults[index]?.result}
                        </span>
                      </div>,
                      document.body
                    )}
                  </div>
                  <button
                    type="button"
                    className="budget-category-remove"
                    onClick={() => removeBudgetCategory(index)}
                    title="Видалити категорію"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3,6 5,6 21,6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      <line x1="10" y1="11" x2="10" y2="17"></line>
                      <line x1="14" y1="11" x2="14" y2="17"></line>
                    </svg>
                  </button>
                    </div>
                  ))}
            </div>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={addBudgetCategory}
              disabled={loadingCategories}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ marginRight: '6px' }}>
                <path d="M12 2v20M2 12h20"></path>
              </svg>
              Додати категорію
            </button>
          </div>

          {formErrors.general && (
            <div className="error-message general-error">
              {formErrors.general}
            </div>
          )}
            </form>
          );
        })()}
      </Modal>

      <ConfirmationModal
        isOpen={showConfirmClose}
        title="Закрити форму?"
        message="У вас є незбережені зміни. Ви впевнені, що хочете закрити форму без збереження?"
        confirmText="Так"
        cancelText="Скасувати"
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
        variant="warning"
      />

      <ConfirmationModal
        isOpen={!!showConfirmDelete}
        title="Видалити бюджет?"
        message="Ви впевнені, що хочете видалити цей бюджет? Цю дію неможливо скасувати."
        confirmText="Так"
        cancelText="Скасувати"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        variant="danger"
      />

      <BudgetTemplateModal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        onBudgetCreated={async () => { await silentRefresh(); }}
      />
    </div>
  );
};

export default BudgetsPage;
